'use client';

import { Card } from '@/components/common/layout/card';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/common/overlay/dialog/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/common/overlay/tooltip/tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ChevronDown, ChevronUp, Info } from 'lucide-react';
import React, { useEffect, useState, useMemo } from 'react';
import { CaseOutcomeDetail, CaseStatus } from './CaseOutcomeDetail';
import { LoadingSkeleton } from './LoadingSkeleton';
import { cn } from '@/lib/utils';

// Mocked default data for the case outcome.
const DEFAULT_CASE_OUTCOME = {
  similarCases: Math.floor(Math.random() * (224 - 32 + 1)) + 32,
  ratings: {
    excellent: Math.floor(Math.random() * (224 - 32 + 1)) + 32,
    fair: Math.floor(Math.random() * (224 - 32 + 1)) + 32,
    good: Math.floor(Math.random() * (224 - 32 + 1)) + 32,
    poor: Math.floor(Math.random() * (224 - 32 + 1)) + 32,
  },
  description: 'No case outcome data available',
  caseStats: {
    totalCases: 300,
    similarCases: 255,
    veryGood: 120,
    fair: 80,
    poor: 55,
  },
  cases: [
    {
      id: '1',
      name: 'Landlord Dispute Resolution',
      location: 'Montreal, QC',
      date: '2023-05-15',
      status: 'Positive',
      fileType: 'pdf',
    },
    {
      id: '2',
      name: 'Rental Agreement Interpretation',
      location: 'Quebec City, QC',
      date: '2023-03-22',
      status: 'Neutral',
      fileType: 'doc',
    },
  ],
};

export const CaseOutcome = ({
  similarCases = DEFAULT_CASE_OUTCOME.similarCases,
  ratings = DEFAULT_CASE_OUTCOME.ratings,
  description = DEFAULT_CASE_OUTCOME.description,
  isLoading = false,
  caseStats = DEFAULT_CASE_OUTCOME.caseStats,
  cases = DEFAULT_CASE_OUTCOME.cases as any,
}: {
  similarCases?: number | Array<{ title: string; description: string }> | null;
  ratings?: {
    excellent: number;
    fair: number;
    good: number;
    poor: number;
  } | null;
  description?: string | null;
  isLoading?: boolean;
  caseStats?: {
    totalCases: number;
    similarCases: number;
    veryGood: number;
    fair: number;
    poor: number;
  };
  cases?: Array<{
    id: string;
    name: string;
    location: string;
    date: string;
    status: 'Positive' | 'Neutral' | 'Negative';
    fileType: 'pdf' | 'doc';
  }>;
}) => {
  // Local state for collapse/expand and modal details.
  const [mounted, setMounted] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState<CaseStatus | 'All'>(
    'All'
  );

  // Ensure safe ratings (using defaults if missing).
  const safeRatings = useMemo(
    () =>
      ratings && typeof ratings === 'object'
        ? {
            excellent: ratings.excellent ?? 0,
            fair: ratings.fair ?? 0,
            poor: ratings.poor ?? 0,
          }
        : DEFAULT_CASE_OUTCOME.ratings,
    [ratings]
  );

  const total = safeRatings.excellent + safeRatings.fair + safeRatings.poor;
  const negativePercentage =
    total === 0 ? 33.33 : (safeRatings.poor / total) * 100;
  const positivePercentage =
    total === 0 ? 33.33 : (safeRatings.excellent / total) * 100;
  const fairPercentage = total === 0 ? 33.33 : (safeRatings.fair / total) * 100;

  const totalRatingPercentage =
    safeRatings.poor > Math.max(safeRatings.fair, safeRatings.excellent)
      ? negativePercentage
      : safeRatings.fair > Math.max(safeRatings.poor, safeRatings.excellent)
        ? fairPercentage
        : positivePercentage;

  const totalRatingClassification =
    totalRatingPercentage <= 33
      ? 'Poor'
      : totalRatingPercentage <= 66
        ? 'Fair'
        : 'Excellent';

  // For display, we map 'Excellent' to 'Good'.
  const totalRatingText =
    totalRatingClassification === 'Excellent'
      ? 'Good'
      : totalRatingClassification;

  // Compute the circle properties for the progress arc.
  const circumference = useMemo(() => 2 * Math.PI * 56, []);
  const dashArray = useMemo(
    () => `${circumference * 0.75} ${circumference}`,
    [circumference]
  );
  const dashOffset = useMemo(
    () => circumference * 0.75 * (1 - totalRatingPercentage / 100),
    [circumference, totalRatingPercentage]
  );

  // Handlers for detail view.
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleFilterChange = (filter: CaseStatus | 'All') =>
    setSelectedFilter(filter);
  const handleDownload = (caseId: string) =>
    console.log(`Downloading case ${caseId}`);

  // Mark the component as mounted (so animations only run after mount).
  useEffect(() => {
    setMounted(true);
  }, []);

  // Helper functions for rating colors and labels.
  const getRatingColor = (type: 'excellent' | 'fair' | 'poor') => {
    switch (type) {
      case 'excellent':
        return 'bg-emerald-100 text-emerald-600 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'fair':
        return 'bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50';
      case 'poor':
        return 'bg-rose-100 text-rose-600 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800/50';
    }
  };

  const getRatingLabel = (type: string) => {
    switch (type) {
      case 'excellent':
        return 'Good';
      case 'fair':
        return 'Fair';
      case 'poor':
        return 'Poor';
      default:
        return type;
    }
  };

  // Get the number of similar cases
  const similarCasesCount = useMemo(() => {
    if (typeof similarCases === 'number') return similarCases;
    if (Array.isArray(similarCases)) return similarCases.length;
    return 0;
  }, [similarCases]);

  if (!mounted) return null;
  if (isLoading) return <LoadingSkeleton />;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className='w-full'
    >
      <Card className='relative w-full overflow-hidden rounded-xl bg-white shadow-md dark:bg-gray-800/50 dark:shadow-gray-900/20'>
        <AnimatePresence mode='wait'>
          {isCollapsed ? (
            // Compact (collapsed) view with animated height
            <motion.div
              key='compact'
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className='w-full p-3'
            >
              <div className='flex w-full items-center justify-between gap-3'>
                {/* Circle with case count and animated progress */}
                <div className='flex items-center gap-3'>
                  <div className='relative h-9 w-9'>
                    <div className='absolute inset-0 rounded-full border border-gray-100 dark:border-gray-700/50' />
                    <svg
                      width='36'
                      height='36'
                      viewBox='0 0 36 36'
                      className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[135deg]'
                    >
                      <circle
                        cx='18'
                        cy='18'
                        r='14'
                        fill='none'
                        stroke='currentColor'
                        strokeOpacity={0.1}
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeDasharray={dashArray}
                        className='text-purple-600 dark:text-purple-400'
                      />
                      <motion.circle
                        cx='18'
                        cy='18'
                        r='14'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeDasharray={dashArray}
                        initial={{ strokeDashoffset: circumference * 0.75 }}
                        animate={{ strokeDashoffset: dashOffset }}
                        transition={{ duration: 1, ease: 'easeInOut' }}
                        className='text-purple-600 dark:text-purple-400'
                      />
                    </svg>
                    <div className='absolute inset-0 flex flex-col items-center justify-center text-center'>
                      <span className='text-sm font-bold text-gray-900 dark:text-gray-100'>
                        {similarCasesCount}
                      </span>
                      <span className='text-[10px] font-medium text-gray-400 dark:text-gray-500'>
                        cases
                      </span>
                    </div>
                  </div>
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-1.5'>
                      <span className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
                        Case Outcome
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className='h-3.5 w-3.5 text-gray-400 transition-colors hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400' />
                          </TooltipTrigger>
                          <TooltipContent className='border-gray-200 bg-white dark:border-gray-700/50 dark:bg-gray-800'>
                            <p className='text-gray-600 dark:text-gray-300'>
                              Based on historical case data and outcomes
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className='text-xs text-gray-500 dark:text-gray-400'>
                      {totalRatingText} outcome
                    </span>
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className='flex flex-wrap justify-end gap-2'>
                  {Object.entries(safeRatings).map(([type, count]) => (
                    <div
                      key={type}
                      className={cn(
                        'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors',
                        getRatingColor(type as 'excellent' | 'fair' | 'poor')
                      )}
                    >
                      <span className='font-semibold'>{count}</span>
                      <span className='text-[10px] font-medium opacity-90'>
                        {getRatingLabel(type)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className='flex items-center gap-2'>
                  <motion.button
                    className='hidden rounded-lg bg-gray-100 p-1.5 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-600/50 md:flex'
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsCollapsed(false)}
                  >
                    <ChevronDown className='h-4 w-4' />
                  </motion.button>
                  <Dialog
                    open={isDetailModalOpen}
                    onOpenChange={setIsDetailModalOpen}
                  >
                    <DialogTrigger asChild>
                      <motion.button
                        className='rounded-lg bg-purple-600 p-1.5 text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600'
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ArrowUpRight className='h-4 w-4' />
                      </motion.button>
                    </DialogTrigger>
                    <DialogContent className='h-[90vh] w-full max-w-[90vw] overflow-hidden border border-gray-200 bg-white p-0 dark:border-gray-700/50 dark:bg-gray-800/95'>
                      <CaseOutcomeDetail
                        stats={caseStats}
                        cases={cases}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        onDownload={handleDownload}
                        onFilterChange={handleFilterChange}
                        selectedFilter={selectedFilter}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </motion.div>
          ) : (
            // Expanded view
            <motion.div
              key='expanded'
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className='w-full p-4'
            >
              <div className='pointer-events-none absolute inset-0 bg-gradient-to-r from-gray-50/80 to-transparent dark:from-gray-800/80' />
              <div className='relative flex w-full flex-col items-start justify-between gap-4 sm:flex-row'>
                <div className='flex w-full flex-col items-start gap-4 sm:flex-row'>
                  <motion.div
                    className='relative ml-0.5'
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className='relative h-16 w-16 rounded-full sm:h-20 sm:w-20'>
                      <div className='absolute inset-0 rounded-full border-[6px] border-gray-100 dark:border-gray-700/50'></div>
                      <svg
                        width='120'
                        height='120'
                        viewBox='0 0 120 120'
                        className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[135deg] scale-75'
                      >
                        <circle
                          cx='60'
                          cy='60'
                          r='40'
                          fill='none'
                          stroke='currentColor'
                          strokeOpacity={0.1}
                          strokeWidth='6'
                          strokeLinecap='round'
                          strokeDasharray={dashArray}
                          className='text-purple-600 dark:text-purple-400'
                        />
                        <motion.circle
                          cx='60'
                          cy='60'
                          r='40'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='6'
                          strokeLinecap='round'
                          strokeDasharray={dashArray}
                          initial={{ strokeDashoffset: circumference * 0.75 }}
                          animate={{ strokeDashoffset: dashOffset }}
                          transition={{ duration: 1, ease: 'easeInOut' }}
                          className='text-purple-600 dark:text-purple-400'
                        />
                      </svg>
                      <div className='absolute inset-0 flex flex-col items-center justify-center text-center'>
                        <span className='text-lg font-bold text-gray-900 dark:text-gray-100 sm:text-xl'>
                          {similarCasesCount}
                        </span>
                        <span className='text-xs font-medium text-gray-400 dark:text-gray-500'>
                          cases
                        </span>
                      </div>
                    </div>
                  </motion.div>
                  <div className='flex-1'>
                    <div className='mb-2 flex items-center gap-1'>
                      <h2 className='text-base font-bold text-gray-900 dark:text-gray-100 sm:text-lg'>
                        Case Outcome
                      </h2>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className='h-4 w-4 text-gray-400 transition-colors hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400' />
                          </TooltipTrigger>
                          <TooltipContent className='border-gray-200 bg-white dark:border-gray-700/50 dark:bg-gray-800'>
                            <p className='text-gray-600 dark:text-gray-300'>
                              Based on historical case data and outcomes
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className='mb-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                      {description || DEFAULT_CASE_OUTCOME.description}
                    </p>
                    <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
                      {Object.entries(safeRatings).map(([type, count]) => (
                        <div
                          key={type}
                          className={cn(
                            'rounded-lg border bg-white p-2 transition-colors dark:bg-gray-800/50',
                            getRatingColor(
                              type as 'excellent' | 'fair' | 'poor'
                            )
                          )}
                        >
                          <div className='flex items-center justify-between'>
                            <span className='text-lg font-bold'>{count}</span>
                            <span className='text-xs text-gray-400 dark:text-gray-500'>
                              Cases
                            </span>
                          </div>
                          <div className='mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium'>
                            {getRatingLabel(type)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='mt-4 flex items-center gap-2 sm:mt-0'>
                  <motion.button
                    className='hidden rounded-lg bg-gray-100 p-1.5 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-600/50 md:flex'
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsCollapsed(true)}
                  >
                    <ChevronUp className='h-4 w-4' />
                  </motion.button>
                  <Dialog
                    open={isDetailModalOpen}
                    onOpenChange={setIsDetailModalOpen}
                  >
                    <DialogTrigger asChild>
                      <motion.button
                        className='rounded-lg bg-purple-600 p-1.5 text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600'
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ArrowUpRight className='h-4 w-4' />
                      </motion.button>
                    </DialogTrigger>
                    <DialogContent className='h-[90vh] w-full max-w-[90vw] overflow-hidden border border-gray-200 bg-white p-0 dark:border-gray-700/50 dark:bg-gray-800/95'>
                      <CaseOutcomeDetail
                        stats={caseStats}
                        cases={cases}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        onDownload={handleDownload}
                        onFilterChange={handleFilterChange}
                        selectedFilter={selectedFilter}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
};

CaseOutcome.displayName = 'CaseOutcome';
